import React, { useState, useRef, useCallback, useEffect } from "react";
import { createAbsoluteUrl } from "utils/constants";

import { Button } from "app/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "app/components/ui/dialog";

interface Props {
  showDialog: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
}

const failureOptions = [
  {
    icon: "/assets/bar-chart.svg",
    altText: "Bar Chart",
    title: "Network compatibility issue",
    description: "Phone might not support our networks",
  },
  {
    icon: "/assets/region-globe.svg",
    altText: "Globe",
    title: "Different region phone",
    description: "Some phones made for other countries don't work in the US",
  },
  {
    icon: "/assets/locked.svg",
    altText: "Locked",
    title: "Security flag",
    description: "Phone may be reported lost or stolen",
  },
  {
    icon: "/assets/mobile-outline.svg",
    altText: "Mobile",
    title: "Model not recognized",
    description: "Phone not yet certified for our network",
  },
];

const whatYouCanDoItems = [
  "Contact your carrier to ensure your phone is unlocked & paid off",
  "Check that your phone was made for the US",
  "Verify your IMEI number",
  "Call us: 1-844-VLEMOBILE",
];

export const IMEICheckFailureDialog = ({
  showDialog,
  setShowDialog,
}: Props) => {
  const [showTopShadow, setShowTopShadow] = useState<boolean>(false);
  const [showBottomShadow, setShowBottomShadow] = useState<boolean>(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  const checkScroll = useCallback(() => {
    if (scrollAreaRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollAreaRef.current;
      const isAtTop = scrollTop === 0;
      const isAtBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 1;
      const hasScrollableContent = scrollHeight > clientHeight;

      setShowTopShadow(!isAtTop && hasScrollableContent);
      setShowBottomShadow(!isAtBottom && hasScrollableContent);
    }
  }, []);

  // Check scroll state on mount, resize, and when content dimensions change
  useEffect(() => {
    // Delay check to allow dialog open animation to complete
    const rafId = requestAnimationFrame(() => {
      checkScroll();
    });
    const timerId = setTimeout(checkScroll, 300);

    window.addEventListener("resize", checkScroll);

    let resizeObserver: ResizeObserver | undefined;
    if (scrollAreaRef.current) {
      resizeObserver = new ResizeObserver(checkScroll);
      resizeObserver.observe(scrollAreaRef.current);
    }

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timerId);
      window.removeEventListener("resize", checkScroll);
      resizeObserver?.disconnect();
    };
  }, [checkScroll, showDialog]);

  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogContent
        className="reltive top-1/2 gap-0 bg-white w-[95%] sm:max-w-[36.8125rem]"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogClose className="absolute z-10 top-4 right-4 rounded-full md:top-5 md:right-5">
          <img
            src="/assets/close-circle.webp"
            className="flex-shrink-0 w-5 h-5 rounded-full opacity-90 md:w-6 md:h-6 hover:opacity-100"
            alt="Close"
          />
        </DialogClose>
        <DialogHeader
          className={`relative z-[1] p-4 sm:p-5 lg:p-6 2xl:p-8 bg-transparent text-left pb-2 sm:pb-2.5 lg:pb-2.5 2xl:pb-2.5 transition-all duration-300 ease-in-out ${
            showTopShadow ? "shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1)]" : ""
          }`}
        >
          <div className="flex justify-center items-center bg-[#FFFAF6] size-[3.625rem] sm:size-16 lg:size-[4.375rem] rounded-xl md:rounded-2xl p-2.5 mb-2.5 md:mb-3">
            <img
              src="/assets/sad-emoji.svg"
              alt="Sad Emoji"
              className="size-6 sm:size-[1.625rem] lg:size-7"
            />
          </div>
          <DialogTitle className="capitalize font-mobile text-black text-xl sm:text-[1.375rem] lg:text-2xl 2xl:text-[1.75rem] font-bold leading-6 sm:leading-7">
            Your phone isn't ready for VLE Mobile yet
          </DialogTitle>
        </DialogHeader>
        <div
          ref={scrollAreaRef}
          onScroll={checkScroll}
          className="max-h-[calc(90vh-20rem)] px-4 sm:px-5 lg:px-6 2xl:px-8 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
        >
          <div className="">
            <h6 className="text-black text-sm sm:text-[0.925rem] lg:text-base font-semibold leading-4 sm:leading-[1.125rem] lg:leading-5 mb-3.5 md:mb-4">
              Here's what might be happening:
            </h6>
            <ul className="flex flex-col gap-[1.125rem] md:gap-5">
              {failureOptions.map((option, index) => (
                <li key={index} className="flex gap-2 items-center">
                  <div className="shrink-0 flex-grow-0 flex justify-center items-center bg-[#F5F5F5] size-11 md:size-12 rounded-lg">
                    <img
                      src={option.icon}
                      alt={option.altText}
                      className="size-[1.125rem] md:size-5"
                    />
                  </div>
                  <div className="flex-1 text-left">
                    <h4 className="font-mobile text-black text-[0.925rem] sm:text-base font-bold leading-5 sm:leading-6">
                      {option.title}
                    </h4>
                    <p className="text-black/50 text-[0.72rem] sm:text-xs font-medium leading-[1.125rem] sm:leading-5">
                      {option.description}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-black/10 w-full h-px mt-6 mb-[1.375rem] sm:my-[1.625rem] lg:my-7" />
          <div className="mb-[1.375rem] sm:mb-6 lg:mb-7">
            <h6 className="text-black text-sm sm:text-[0.925rem] lg:text-base font-semibold leading-4 sm:leading-[1.125rem] lg:leading-5 mb-3 md:mb-3.5">
              What you can do:
            </h6>
            <ul className="space-y-3 md:space-y-3.5">
              {whatYouCanDoItems.map((item, index) => (
                <li key={index} className="flex gap-2 items-center">
                  <div className="shrink-0 size-4 md:size-[1.125rem]">
                    <img
                      src={
                        item.includes("1-844-VLEMOBILE")
                          ? "/assets/call-calling-green.svg"
                          : "/assets/check-circle-broken.svg"
                      }
                      alt={
                        item.includes("1-844-VLEMOBILE")
                          ? "Calling"
                          : "Check Circle"
                      }
                      className="size-full"
                    />
                  </div>
                  <p className="text-[0.825rem] md:text-sm font-medium leading-[1.125rem] text-black md:leading-5">
                    {item.includes("1-844-VLEMOBILE") ? (
                      <span className="flex gap-1 items-center">
                        Call us:
                        <span
                          className="text-[#FE4D01] font-semibold cursor-pointer hover:text-[#E44501] underline-offset-2 hover:decoration-[#E44501] hover:underline transition-colors"
                          onClick={() =>
                            (window.location.href = "tel:18448536624")
                          }
                        >
                          1-844-VLEMOBILE
                        </span>
                      </span>
                    ) : (
                      item
                    )}
                  </p>
                </li>
              ))}
            </ul>
          </div>
          <div
            className="rounded-xl text-center p-3.5 sm:p-4 lg:p-5 lg:pt-[1.19rem]"
            style={{
              background:
                "linear-gradient(94deg, rgba(255, 173, 138, 0.1) 0%, rgba(253, 253, 253, 0.8) 70%, #FFF5ED 100%)",
            }}
            // style={{
            //   background: "linear-gradient(94deg, rgba(255, 173, 138, 0) -17.89%, rgba(253, 253, 253, 0.58) 136.54%, #FFF5ED 142.14%)",
            // }}
          >
            <p className="max-w-[24.3125rem] text-[0.825rem] md:text-sm font-medium leading-5 sm:leading-6 text-black mx-auto">
              Don't worry! Most issues can be fixed. We'll help you join VLE
              Mobile soon!
            </p>
          </div>
        </div>
        <div
          className={`relative z-[1] grid grid-cols-2 gap-3 sm:gap-3.5 lg:gap-4 p-4 sm:p-5 lg:p-6 2xl:p-8 [@media(max-width:400px)]:pt-3.5 pt-4 sm:pt-5 md:pt-6 lg:pt-7 2xl:pt-7 transition-all duration-300 ease-in-out ${
            showBottomShadow ? "shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]" : ""
          }`}
        >
          <Button
            type="button"
            variant="primary"
            className="h-11 sm:h-12 md:h-[3.375rem]"
            onClick={() => {
              setShowDialog(false);
              window.open(
                createAbsoluteUrl("/support-center"),
                "_blank",
                "noopener,noreferrer",
              );
            }}
          >
            {/* w-[62.85%] */}
            <img
              src="/assets/call-calling-white.svg"
              alt="Call Support"
              className="size-3 sm:size-3.5 md:size-4 mr-1.5"
            />
            Call Support
          </Button>
          <Button
            type="button"
            variant="outline"
            className="h-11 sm:h-12 md:h-[3.375rem] lg:h-[3.375rem] border-[#E9EAEB] text-black"
            onClick={() => setShowDialog(false)}
          >
            {/* w-[37.15%] */}
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
